import {PagePreloader} from "ui/Preloader";
import {rootStore, useStores} from "stores/rootStore";
import {IUserSettings, Plan} from "types";
import React, {useEffect, useLayoutEffect, useState} from "react";
import {formatPercent, translate} from "i18n/translator";
import {ltr} from "i18n/bidi";
import type {FluentVariable} from "@fluent/bundle";
import {SUPPORTED_LANGUAGES, SupportedLanguage} from "i18n/constants";
import {COMPACT_MAX_WIDTH, LANGUAGE_IN_SIDEBAR_MAX_WIDTH, MOBILE_MAX_WIDTH, upsellCandidates} from "config";
import {safeRandomUUID} from "lib/cryptoFallback";

export type ErrorType = {
    code: string;
    message: string;
    translationArgs?: Record<string, FluentVariable>;
}

/** Backend error payloads carry the numbers behind the failure alongside the code
 *  (`{code, message, limit, pages}` — see `error_data` in bitapi/errors.py). The
 *  English `message` already names them; our translation of the same code can only
 *  do so if they survive the trip, so everything scalar that isn't the code or the
 *  message becomes a Fluent argument for `error_type__<code>`. */
const errorArgs = (payload: Record<string, any>): Record<string, FluentVariable> | undefined => {
    const args: Record<string, FluentVariable> = {};
    for (const [key, value] of Object.entries(payload)) {
        if (key === "code" || key === "message") continue;
        if (typeof value === "string" || typeof value === "number") args[key] = value;
    }
    return Object.keys(args).length ? args : undefined;
}

export const reprNetworkError = (error: any, prefix = ""): ErrorType => {

    if (typeof error === "string") {
        return {
            code: "custom",
            message: prefix + error
        }
    }
    // Backend message
    const data = error?.response?.data
    if (data?.error) {
        return {
            code: data.error.code,
            message: prefix + data.error.message,
            translationArgs: errorArgs(data.error),
        }
    }

    // Axios message
    if (error?.message) {

        return {
            code: "network",
            message: prefix + error?.message,
        }
    }
    return {
        code: "network",
        message: prefix + "Unknown error"
    }
}

export const getToken = () => {
    return rootStore.user.token
}

export const hasToken = () => {
    return !!getToken()
}

export function throttle<T>(func: Function, delay = 3000) {
    let shouldWait = false;

    return function (this: T, ...args: any[]) {
        if (!shouldWait) {
            func.apply(this, args);
            shouldWait = true;
            setTimeout(() => {
                shouldWait = false;
            }, delay);
        }
    };
}

export function once<T>(delay = 3000) {
    let shouldWait = false;

    return function (this: T) {
        if (!shouldWait) {
            shouldWait = true;
            setTimeout(() => {
                shouldWait = false;
            }, delay);
            return true;
        }
        return false;
    };
}



export function getClientIdFromCookie() {
    const GA_KEY = "IA__GA_CLIENT_ID";
    if (sessionStorage.getItem(GA_KEY)) {
        return sessionStorage.getItem(GA_KEY);
    }
    const cookie = document.cookie.split('; ').find(row => row.startsWith('_ga='));
    if (!cookie) return null;
    const gaCookie = cookie.split('=')[1];
    const parts = gaCookie.split('.');
    const clientID =  parts.length >= 3 ? parts[2] + "." + parts[3] : null;
    if (!!clientID) {
        sessionStorage.setItem(GA_KEY, clientID);
    }
    return clientID;
}

// Deprecated. Now use ai score as is.
export const getConfidence = (score?: number) => {
    if (isNully(score)) return undefined;
    return Math.max(score!, 1 - score!);
}

export function getErrorText(errorText: string, isPremium?: boolean) {
    if (isPremium) {
        return translate('error-limit-reached', undefined, 'Limit reached');
    } else {
        return translate('error-limit-reached-free', undefined, 'Free plan words limit reached. Get premium plan to increase your limits');
    }
}

export function validateEmail(email: string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export function paramsGet(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
}

/**
 * Drops the given query params in at most one URL change (each is a new Clarity page), keeping
 * the entry's state: the router's keys and the workspace stamp.
 */
export function paramsRemove(...keys: string[]) {
    const url = new URL(window.location.href);
    if (!keys.some(key => url.searchParams.has(key))) return;
    keys.forEach(key => url.searchParams.delete(key));

    const next =
        url.pathname +
        (url.searchParams.toString()
            ? `?${url.searchParams.toString()}`
            : '') +
        url.hash;

    window.history.replaceState(window.history.state, '', next);
}

export function getAnchor(): string | null {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
        return hash.substring(1); // Remove the '#' prefix
    }
    return null;
}

export function removeAnchor() {
    const url = new URL(window.location.href);
    url.hash = '';
    
    const next = url.pathname + 
        (url.searchParams.toString() 
            ? `?${url.searchParams.toString()}` 
            : '');
    
    window.history.replaceState(null, '', next);
}



export const useUser = () => {
    const {user} = useStores()
    return user.user as IUserSettings
}

// Helper function to split text into sentences
export const splitIntoSentences = (text: string): string[] => {
    return text.match(/[^.!?]+[.!?]+/g) || [];
};

export const openInNewTab = (url: string) => {
    if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer')
    }
}

export const Line: React.FC<{
    children: React.ReactNode,
    gap?: number,
    align?: string,
    justify?: string,
    center?: boolean,
    style?: React.CSSProperties,
    className?: string
    fullWidth?: boolean
    ref?: React.Ref<HTMLDivElement>
} & React.HTMLAttributes<HTMLDivElement>> = ({
                                                 children,
                                                 gap,
                                                 align = null,
                                                 justify = "flex-start",
                                                 center = true,
                                                 style = {},
                                                 className,
                                                                    fullWidth=false,
                                                 ...props
                                             }) => (
    <div style={{
        display: "flex",
        gap: gap,
        alignItems: align || (center ? "center" : 'flex-start'),
        justifyContent: justify, ...style,
        width: fullWidth ? "100%" : "auto"
    }} {...props}
         className={className}>
        {children}
    </div>
)

export const MLine = Line;

export const Text: React.FC<{
    children: React.ReactNode,
    color?: 'secondary' | 'tertiary' | 'primary' | 'accent' | string,
    size?: number,
    weight?: number,
    style?: React.CSSProperties,
    className?: string,
    element?: string,
    height?: number,
    letterSpacing?: number,
    dir?: 'auto' | 'ltr' | 'rtl'
}>
    = ({
           children,
           color = null,
           size = null,
           weight = null,  // regular(400), light(300), medium(500), bold(700)
           style = {},
           className = "",
           element = "div",
           height = null,
           letterSpacing = null,
           ...props
       }) => {
    // element to react component
    if (!!height) {
        style.lineHeight = height
    }
    if (!!size) {
        style.fontSize = size + "px"
    }
    if (!!weight) {
        style.fontWeight = weight
    }
    if (!!letterSpacing) {
        style.letterSpacing = letterSpacing + "px"
    }
    return React.createElement(element, {
        style: {
            color: {
                secondary: "#494B52",
                tertiary: "#A6A8B0",
                primary: "#080C1E",
                accent: "#6B84F0"
            }[color || ""] || color, ...style
        }, className: "util-text " + className, ...props
    }, children)
}

export const MText = Text;

export const MBold: React.FC<{ children: React.ReactNode, className?: string }> = ({children, className, ...props}) => (
    <span style={{fontWeight: 700}} className={className} {...props}>
    {children}
  </span>
)

export const MLight: React.FC<{ children: React.ReactNode, className?: string }> = ({children, className}) => <span
    className={"text-light " + className}>{children}</span>
export const MAccent: React.FC<{ children: React.ReactNode }> = ({children}) => <span
    className={"font-accent"}>{children}</span>

interface MStackProps {
    flex?: string
    children: React.ReactNode
    justify?: string
    gap?: number | string
    align?: string
    center?: boolean
    ref?: React.Ref<HTMLDivElement>
    style?: React.CSSProperties
    className?: string
}

export const MStack: React.FC<MStackProps & React.HTMLAttributes<HTMLDivElement>>
    = ({flex, children, gap = 0, className, justify, align = 'flex-start', center = false, style, ...props}) => {
    style = style || {}
    if (!!justify) {
        style.justifyContent = justify
    }
    if (!!flex) {
        style.flex = flex
    }

    return <div className={"util-stack " + className}
                style={{gap: gap + (typeof gap === "number" ? "px" : ""), alignItems: center ? "center" : align, ...style}} {...props}>{children}</div>
}

const getCurrentLocale = () => {
    if (typeof document !== 'undefined') {
        const attr = document.documentElement.getAttribute('lang');
        if (attr) {
            return attr;
        }
    }
    return 'en';
};

const normalizeLocale = (locale: string) => {
    if (locale === 'en') {
        return 'en-GB';
    }
    // Western digits, as everywhere in the Arabic UI: plain `ar` is Arabic-Indic in some engines.
    if (locale === 'ar') {
        return 'ar-u-nu-latn';
    }
    return locale;
};

const startOfDay = (date: Date) => {
    const clone = new Date(date);
    clone.setHours(0, 0, 0, 0);
    return clone;
};

export const dateIsoToReadable = (isoString: string | null | undefined, compact=false) => {
    const date = new Date(isoString ?? NaN);
    // A missing or unparsable date renders as nothing: Intl.DateTimeFormat throws
    // on an invalid Date, and a field the backend has not sent must not take the
    // page down.
    return Number.isNaN(date.getTime()) ? '' : dateToReadable(date, compact);
}

export const dateIsoToTime = (isoString: string) => {
    // 09:12:32 GMT+3
    const date = new Date(isoString);
    const locale = normalizeLocale(getCurrentLocale());
    return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

export const isoToDateTime = (isoString: string) => {
    const date = new Date(isoString);
    const dateString = date.toLocaleDateString();
    return dateString + " at " + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

/** A date in the UI language's own locale, with whatever fields the caller asks for
 *  ("Aug 17", "August 17", "3:05 PM"). Never throws: an odd locale falls back to the
 *  runtime default rather than taking the component down. */
export const formatDate = (date: Date, opts: Intl.DateTimeFormatOptions) => {
    try {
        return new Intl.DateTimeFormat(normalizeLocale(getCurrentLocale()), opts).format(date);
    } catch {
        return new Intl.DateTimeFormat(undefined, opts).format(date);
    }
}

export const dateToReadable = (date: Date, compact=false) => {
    const lang = getCurrentLocale();
    const locale = normalizeLocale(lang);

    if (compact) {
        return ltr(new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: "2-digit",
            year: "2-digit"
        }).format(date));
    }
    const today = startOfDay(new Date());
    const target = startOfDay(date);
    const diffDays = Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

    if (Math.abs(diffDays) <= 1) {
        try {
            const relativeFormatter = new Intl.RelativeTimeFormat(locale, {numeric: 'auto'});
            const relative = relativeFormatter.format(diffDays, 'day');
            return locale.startsWith('en') ? capitalize(relative) : relative;
        } catch (error) {
            // fall through to absolute formatting
        }
    }

    const monthStyle: Intl.DateTimeFormatOptions['month'] = locale.startsWith('en') ? 'short' : 'long';
    const formatter = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: monthStyle,
        year: 'numeric',
    });
    let formatted = formatter.format(date);

    if (locale.startsWith('en')) {
        formatted = formatted.replace(/ (\p{L}{3,}) /u, ' $1, ');
    }

    return formatted;
}

export const toSubKey = (name?: string): Plan | null => {
    if (!name) {
        return null;
    }
    return name.toLowerCase().replaceAll(" ", "_") as Plan;
}

export const toSubName = (key: Plan): string => {
    const fallbacks: Record<string, string> = {
        "pay_as_you_go": "Pay as you go",
        "auto_pay": "Auto pay",
        "one_time_scan": "One-time scan",
        "plus": "Plus",
        "pro": "Pro",
        "enterprise": "Enterprise",
        "premium": "Premium",
        "free": "Free",
        "educational": "Educational",
        "detector_tool": "Detector tool",
    };
    const translationKey = `subscription-plan-label-${key}`;
    return translate(translationKey, undefined, fallbacks[key]);
}

// Next tier in the upgrade funnel. Null at the top tier or for plans outside the
// ladder — see upsellCandidates (config), which owns the funnel's policy.
export const getNextPlan = (currentPlan: Plan | null): Plan | null =>
    upsellCandidates(isNully(currentPlan) ? null : currentPlan!)[0] ?? null;

export const refreshWait = (cb: any, count = 5, wait = 1000) => {
    let i = 0
    const interval = setInterval(async () => {
        cb()
        i += 1
        if (i >= count) {
            clearInterval(interval)
        }
    }, wait)
}

export function capitalize(str: string) {
    if (!str) return '';
    return str[0].toUpperCase() + str.slice(1);
}

/** The embedded widget surface — the one route the router serves under `widgets/`
 *  (`/<lang>/widgets/scan`, iframed by aichecker and aidetector), plus the language-less form
 *  the bare-route redirect starts from. Matched as a whole path, not as a `/widgets/`
 *  substring: any other path under `widgets/` has no route of its own and falls through to
 *  the unknown-route redirect, which renders the normal app — and a substring match had
 *  already told it to skip AuthProvider, so the first `useAuth()` crashed the page. That is
 *  `/en/widgets/`, which crawlers walk up to from the iframe src. */
const WIDGET_ROUTE = new RegExp(`^(?:/(?:${SUPPORTED_LANGUAGES.join('|')}))?/widgets/scan/?$`);
export const isWidgetRoute = () => WIDGET_ROUTE.test(window.location.pathname);

/** A report route renders a stored analysis, so the scan page's own state — a restored
 *  draft, deep-link flags, a landing handoff — must not be applied over it. Matches the
 *  localized paths too (`/ar/report/<id>`). */
export const isReportRoute = () => window.location.pathname.includes('/report/');

/** The scan page itself (`/<lang>/main/scan`, where every unknown path redirects) — the only
 *  route whose URL carries the scan-mode selection. Not just "wherever ScanPage renders":
 *  the OAuth callback page renders it too, behind the handler, and its URL carries the auth
 *  code rather than a selection to restore. */
export const isScanRoute = () => window.location.pathname.includes('/main/scan');

export const isExtensionRoute = () => /^\/[a-z]{2}\/extension(\/|$)/.test(window.location.pathname);

export const LoadingFallback = () => (
    <div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", width: "100%"}}>
        <PagePreloader/>
    </div>
);

export const FullScreenLoader: React.FC<{ loading: boolean }> = ({loading}) => {
    if (!loading) return null;

    return (
        <div className={'full-screen'}>
            <PagePreloader/>
        </div>
    );
};

export const BorderBox: React.FC<{ children: React.ReactNode, className?: string } & React.HTMLAttributes<HTMLDivElement>> =
    ({children, className, ...props}) => {
        return <div className={'border-box ' + className} {...props}>
            {children}
        </div>
    }

export function useWindowWidthThreshold(
    threshold: number,
    onBelow: () => void,
    onAbove?: () => void
) {
    useEffect(() => {
        let below = window.innerWidth < threshold;

        function handleResize() {
            const isBelow = window.innerWidth < threshold;
            if (isBelow && !below) {
                onBelow();
            } else if (!isBelow && below && onAbove) {
                onAbove();
            }
            below = isBelow;
        }

        window.addEventListener('resize', handleResize);
        // fire once on mount
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [threshold, onBelow, onAbove]);
}

export function splitByPlagiated(text: string, plagiatedTexts: { text: string, probability: number }[]) {
    const result: Array<{ text: string, type: "plagiat" | "original", probability?: number }> = [];

    const plagiatSentences = new Map<string, number>();
    const textProbabilities = new Map<string, number[]>();

    for (const plagText of plagiatedTexts) {
        const normalized = plagText.text.toLowerCase().replace(/[^\p{L}]/gu, '');
        if (normalized) {
            if (!textProbabilities.has(normalized)) {
                textProbabilities.set(normalized, []);
            }
            const probArray = textProbabilities.get(normalized);
            if (probArray) {
                probArray.push(plagText.probability);
            }
        }
    }

    const normalizedTexts = Array.from(textProbabilities.keys());
    for (let i = 0; i < normalizedTexts.length; i++) {
        const normalized = normalizedTexts[i];
        const probabilities = textProbabilities.get(normalized);
        if (probabilities && probabilities.length > 0) {
            let sum = 0;
            for (let j = 0; j < probabilities.length; j++) {
                sum += probabilities[j];
            }
            const average = sum / probabilities.length;
            plagiatSentences.set(normalized, average);
        }
    }

    const delimiters = /([.])/;
    const parts = splitIntoSentences(text);

    let currentSentence = "";
    for (let i = 0; i < parts.length; i++) {
        currentSentence += parts[i];
        if (delimiters.test(parts[i]) || i === parts.length - 1) {
            if (/\S/.test(currentSentence)) {
                const trimmedSentence = currentSentence.trim();
                const normalized = trimmedSentence.toLowerCase().replace(/[^\p{L}]/gu, '');
                let matchingProbabilities = [];
                for (const plagiatSentence of Array.from(plagiatSentences.keys())) {
                    if (normalized.includes(plagiatSentence)) {
                        matchingProbabilities.push(plagiatSentences.get(plagiatSentence)!);
                    }
                }
                const probability = matchingProbabilities.length > 0
                    ? matchingProbabilities.reduce((sum, prob) => sum + prob, 0) / matchingProbabilities.length
                    : 0;

                if (probability !== 0) {
                    result.push({
                        text: currentSentence,
                        type: "plagiat",
                        probability: probability
                    });
                } else {
                    result.push({
                        text: currentSentence,
                        type: "original"
                    });
                }
            }
            currentSentence = "";
        }
    }
    return result;
}

export const sleep = async (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export const suppressNetworkError = async (func: Promise<any>) => {
    try {
        return await func;
    } catch (e) {
        rootStore.setError();
        console.error(e);
    }
}

/** An element the user types into — an input, a textarea or any contentEditable host.
 *  Page-level keyboard handlers use it to stay out of fields that are not theirs. */
export const isEditableElement = (el: EventTarget | Element | null): el is HTMLElement => {
    const node = el as HTMLElement | null;
    if (!node || !node.tagName) return false;
    const tag = node.tagName.toLowerCase();
    return tag === "input" || tag === "textarea" || !!node.isContentEditable;
};

/** Whether the viewport is at most `breakpoint` px wide. */
export const useDevice = (breakpoint: number) => useMaxWidth(breakpoint);

export const useMobile = (breakpoint = MOBILE_MAX_WIDTH) => {
    return useDevice(breakpoint)
};

export const useCompact = (breakpoint = MOBILE_MAX_WIDTH) => {
    return useDevice(breakpoint)
};

/** Whether `(max-width: <px>)` holds — re-renders on crossings only, not on every resize event. */
export const useMaxWidth = (maxWidth: number) => {
    const query = `(max-width: ${maxWidth}px)`;
    // jsdom has no matchMedia: there the answer is "wide", as the old resize hook's was.
    const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
    const [matches, setMatches] = useState(() => supported && window.matchMedia(query).matches);
    useEffect(() => {
        if (!supported) return;
        const mql = window.matchMedia(query);
        const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
        setMatches(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query, supported]);
    return matches;
};

/** Widths where the sidebar starts collapsed and expands over the content. */
export const useCompactSidebar = () => useMaxWidth(COMPACT_MAX_WIDTH);

/** Widths where an account's header has no room for the language switcher, so the sidebar
 * carries it instead. An anon's header always has room. */
export const useLanguageInSidebar = () => {
    const narrow = useMaxWidth(LANGUAGE_IN_SIDEBAR_MAX_WIDTH);
    const {user} = useStores();
    return narrow && !user.isAnonymous;
};



export const formatBigNumbers = (number: string) => {
    if (!number) {
        return null
    }
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A count with thousands separators — the counter pills and the plan numbers in copy. */
export const formatCount = (n: number) => formatBigNumbers(String(n)) ?? String(n);

/** "2,503 / 100,000": used of total, one LTR token — an RTL line must not read it as total / used. */
export const formatCountOf = (count: number, total: number) => ltr(`${formatCount(count)} / ${formatCount(total)}`);

/** 300000 → "300k" (en) — the short form the sidebar promo and the tabs use.
 *  Chart axes pass maximumFractionDigits: 1, or 1500 and 2000 both read "2k". */
export const formatCompactNumber = (value: number, lang: string, maximumFractionDigits = 0) => {
    try {
        const text = new Intl.NumberFormat(lang, {notation: 'compact', maximumFractionDigits}).format(value);
        return lang === 'en' ? text.replace('K', 'k') : text;
    } catch {
        return String(value);
    }
}

export function addDays(day_count: number) {
    const now = new Date();
    now.setDate(now.getDate() + day_count);
    return now;
}

export function getUUID() {
    return safeRandomUUID();
}

export function sendEmail(mailAddr: string) {
    var mail = 'mailto:' + mailAddr;
    var a = document.createElement('a');
    a.href = mail;
    a.click();
};

export const getNowScannedString = (): string => {
    const now = new Date();
    return formatDate(now, {year: 'numeric', month: 'numeric', day: 'numeric'}) + ' '
        + formatDate(now, {hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false});
}

/** Puts `text` on the clipboard — or a text still on its way. Resolves whether it got there. */
export const copyClipboard = (text: string | Promise<string>): Promise<boolean> => {
    const clipboard = navigator.clipboard;
    if (!clipboard) return Promise.resolve(false);
    const write = typeof text === 'string' ? clipboard.writeText(text)
        // Safari lets a click write the clipboard only from within the click: a text fetched
        // after it has to go as a ClipboardItem taking the pending text.
        : typeof ClipboardItem !== 'undefined'
            ? clipboard.write([new ClipboardItem({'text/plain': text.then(t => new Blob([t], {type: 'text/plain'}))})])
            : text.then(t => clipboard.writeText(t));
    // The clipboard rejects when the document isn't focused or permission is denied; an
    // unhandled rejection here surfaced as a runtime-error overlay over the whole page.
    return write.then(() => true, err => {
        console.warn("clipboard write failed", err);
        return false;
    });
}

export const isNully = (value: any): boolean => {
    return value === null || value === undefined || value === false || (typeof value === "number" && isNaN(value))
}

export const usePropsBasedState = (value: any) => {
    const [state, setState] = useState(value);
    useLayoutEffect(() => {
        setState(value)
    }, [value]);

    return [state, setState];
}

export const scoreToPercent = (score: number | null | undefined, {percent=true, round=false, nonZero=true} = {}): string => {
    if (isNully(score)) return "-";
    const value = score! * 100;
    if (!percent) {
        const fractionDigits = round ? 0 : 1;
        let formatted = value.toFixed(fractionDigits);
        if (formatted.endsWith('.0')) {
            formatted = formatted.slice(0, -2);
        }
        if (formatted === '0' && nonZero && !round) {
            return scoreToPercent(score, {percent, round: false, nonZero: false});
        }
        return formatted;
    }
    return formatPercent(value, round ? 0 : 1, nonZero);
}

export const checkError = (error: any, spec: string) => {
    if (!error) return false;
    if (typeof error !== 'string') return false;
    return error.includes(spec)
}

/** Map a backend limit-error code to the paywall trigger that explains it.
 *
 * The trigger is derived purely from the error code — we no longer force the
 * deep-scan trigger just because a deep scan was running: framing an
 * unrelated limit (words, daily, etc.) as "deep scans" would be wrong. */
export const limitErrorToTrigger = (code?: string):
    'plagiarism_limit' | 'deep_scan_limit' | 'grammar_limit' | 'words_limit_reached' | 'humanizer_words' => {
    if (checkError(code, 'plagiarism_attempts')) return 'plagiarism_limit';
    if (checkError(code, 'grammar_attempts')) return 'grammar_limit';
    if (checkError(code, 'deepscan_limit')) return 'deep_scan_limit';
    if (checkError(code, 'humanizer_words_limit')) return 'humanizer_words';
    return 'words_limit_reached';
}

export const isSupportedLanguage = (value?: string | null): value is SupportedLanguage => {
    if (!value) {
        return false;
    }
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
};

export const filterOnlySupported = (lang?: string | null): SupportedLanguage | null => {
    if (!lang) return null;
    if (isSupportedLanguage(lang)) return lang;
    return null;
}

export const isLanguagePrefixedRoute = (route: string): boolean => {
    return SUPPORTED_LANGUAGES.some((language) => {
        const prefix = `/${language}`;
        return route === prefix || route.startsWith(`${prefix}/`) || route.startsWith(`${prefix}?`);
    });
};

export const toShortLink = (url: string) => {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname;
    } catch {
        return url;
    }
};

export const isIframeBased = () => {
    return window.parent !== window;
}